'use client';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { ALL_TOOLS, Tool } from '@/lib/toolRegistry';
import { recordToolVisit } from '@/app/home/components/RecentlyUsedCarousel';

const AUDIO_TOOLS = ALL_TOOLS.filter((t) => t.category === 'Audio Tools');

// Deduplicate by route (keep last occurrence which is the more detailed one)
const UNIQUE_AUDIO_TOOLS: Tool[] = Object.values(
  AUDIO_TOOLS.reduce<Record<string, Tool>>((acc, tool) => {
    acc[tool.route] = tool;
    return acc;
  }, {})
);

const FILTER_TAGS = [
  { label: 'All Tools', value: 'all' },
  { label: 'Convert', value: 'convert' },
  { label: 'Edit', value: 'edit' },
  { label: 'Record', value: 'record' },
  { label: 'Effects', value: 'effects' },
  { label: 'Utility', value: 'utility' },
];

const TOOL_TAG_MAP: Record<string, string[]> = {
  'audio-converter': ['convert'],
  'audio-trimmer': ['edit'],
  'audio-merger': ['edit'],
  'volume-booster': ['effects'],
  'audio-speed-changer': ['effects'],
  'pitch-changer': ['effects'],
  'voice-recorder': ['record'],
  'extract-audio': ['convert'],
  'extract-audio-from-video': ['convert'],
  'ringtone-maker': ['edit', 'utility'],
  'fade-audio': ['effects'],
  'audio-fade': ['effects'],
  'remove-silence': ['edit', 'utility'],
  'audio-metadata-remover': ['utility'],
};

const FAVORITES_KEY = 'audio-tools-favorites';

function loadFavorites(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveFavorites(favorites: Set<string>) {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(Array.from(favorites)));
  } catch {
    // ignore
  }
}

function matchesTag(tool: Tool, tag: string): boolean {
  if (tag === 'all') return true;
  const tags = TOOL_TAG_MAP[tool.id] ?? [];
  return tags.includes(tag);
}

export default function AudioToolsGalleryPage() {
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState('all');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  // Load favorites from localStorage on mount
  useEffect(() => {
    setFavorites(loadFavorites());
  }, []);

  const toggleFavorite = useCallback((toolId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(toolId)) {
        next.delete(toolId);
      } else {
        next.add(toolId);
      }
      saveFavorites(next);
      return next;
    });
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const results = UNIQUE_AUDIO_TOOLS.filter((tool) => {
      const matchesSearch =
        !q ||
        tool.title.toLowerCase().includes(q) ||
        tool.description.toLowerCase().includes(q) ||
        tool.keywords.some((k) => k.toLowerCase().includes(q));
      return matchesSearch && matchesTag(tool, activeTag);
    });

    // Sort: favorites first, then alphabetical within each group
    return [...results].sort((a, b) => {
      const aFav = favorites.has(a.id) ? 0 : 1;
      const bFav = favorites.has(b.id) ? 0 : 1;
      return aFav - bFav;
    });
  }, [query, activeTag, favorites]);

  const favoriteCount = favorites.size;

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-text-muted mb-6">
            <Link href="/" className="hover:text-brand-blue transition-colors">Home</Link>
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-text-secondary font-medium">Audio Tools</span>
          </nav>

          {/* Title block */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0" style={{ backgroundColor: '#FFFBEB' }}>
              🎵
            </div>
            <div>
              <h1 className="section-title text-3xl sm:text-4xl text-text-primary mb-1">Audio Tools</h1>
              <p className="text-text-secondary text-sm sm:text-base max-w-xl">
                {UNIQUE_AUDIO_TOOLS.length} browser-based tools to convert, edit, record and enhance audio — no uploads, no accounts, fully private.
              </p>
              {favoriteCount > 0 && (
                <p className="text-xs text-amber-600 mt-1 font-medium">
                  ⭐ {favoriteCount} favorite{favoriteCount !== 1 ? 's' : ''} pinned to the top
                </p>
              )}
            </div>
          </div>

          {/* Search bar */}
          <div className="relative max-w-xl">
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search audio tools…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue transition-all"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                aria-label="Clear search"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter tags */}
      <div className="bg-white border-b border-gray-100 sticky top-14 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            {FILTER_TAGS.map((tag) => (
              <button
                key={tag.value}
                onClick={() => setActiveTag(tag.value)}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 ${
                  activeTag === tag.value
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'bg-gray-100 text-text-secondary hover:bg-amber-50 hover:text-amber-700'
                }`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tool grid */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-4xl mb-3">🔍</div>
            <p className="text-text-secondary font-medium">No tools match &ldquo;{query}&rdquo;</p>
            <button
              onClick={() => { setQuery(''); setActiveTag('all'); }}
              className="mt-3 text-sm text-brand-blue hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs text-text-muted mb-5">
              Showing <span className="font-semibold text-text-secondary">{filtered.length}</span> of {UNIQUE_AUDIO_TOOLS.length} tools
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filtered.map((tool) => (
                <AudioToolCard
                  key={tool.id}
                  tool={tool}
                  isFavorite={favorites.has(tool.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Privacy note */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4">
          <span className="text-xl flex-shrink-0">🔒</span>
          <p className="text-sm text-amber-800">
            <span className="font-semibold">100% private.</span> All audio processing runs directly in your browser using WebAssembly. Your files are never uploaded to any server.
          </p>
        </div>
      </div>
    </div>
  );
}

interface AudioToolCardProps {
  tool: Tool;
  isFavorite: boolean;
  onToggleFavorite: (toolId: string) => void;
}

function AudioToolCard({ tool, isFavorite, onToggleFavorite }: AudioToolCardProps) {
  const isDisabled = tool.status === 'coming-soon';

  const handleClick = () => {
    if (!isDisabled) recordToolVisit(tool.id);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite(tool.id);
  };

  const card = (
    <div
      className={`tool-card bg-white rounded-2xl p-4 border flex flex-col h-full relative overflow-hidden transition-all duration-200 ${
        isFavorite ? 'border-amber-300' : 'border-gray-100'
      } ${isDisabled ? 'opacity-60 cursor-default' : 'cursor-pointer group'}`}
      style={{ boxShadow: isFavorite ? '0 1px 3px rgba(217,119,6,0.15), 0 2px 8px rgba(217,119,6,0.08)' : '0 1px 3px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)' }}
    >
      {/* Favorite button */}
      <button
        onClick={handleFavoriteClick}
        aria-label={isFavorite ? `Remove ${tool.title} from favorites` : `Add ${tool.title} to favorites`}
        className={`absolute top-2.5 right-2.5 z-10 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-200 ${
          isFavorite
            ? 'text-amber-500 bg-amber-50 hover:bg-amber-100' :'text-gray-300 bg-transparent hover:text-amber-400 hover:bg-amber-50'
        }`}
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill={isFavorite ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={isFavorite ? 0 : 2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
          />
        </svg>
      </button>

      {tool.status === 'beta' && (
        <span className="absolute top-2.5 left-2.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
          Beta
        </span>
      )}

      {/* Icon */}
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-3 transition-transform duration-200 group-hover:scale-110"
        style={{ backgroundColor: '#FFFBEB' }}
      >
        <span role="img" aria-label={tool.title}>{tool.icon}</span>
      </div>

      {/* Title */}
      <h3 className="font-semibold text-text-primary text-sm leading-tight mb-1.5 group-hover:text-amber-600 transition-colors pr-6">
        {tool.title}
      </h3>

      {/* Description */}
      <p className="text-text-muted text-xs leading-snug line-clamp-2 flex-1 mb-3">
        {tool.description}
      </p>

      {/* CTA */}
      <div
        className={`w-full py-1.5 px-3 rounded-xl text-xs font-semibold text-center transition-all duration-200 ${
          isDisabled
            ? 'bg-gray-200 text-gray-400 cursor-default' :'text-white group-hover:opacity-90 group-hover:shadow-md'
        }`}
        style={isDisabled ? undefined : { background: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)' }}
      >
        {isDisabled ? 'Coming Soon' : 'Open Tool →'}
      </div>
    </div>
  );

  if (isDisabled) return <div>{card}</div>;
  return (
    <Link href={tool.route} onClick={handleClick} className="block group h-full">
      {card}
    </Link>
  );
}
